import { useState, useEffect } from 'react';
import { Plus, MessageSquare, Folder, Search, MoreVertical, Trash2, Edit2, ChevronDown, ChevronUp, X, Eye, Loader2, Save, SaveAll, Sparkles, ChevronLeft, ChevronRight } from 'lucide-react';
import { useCompactMode } from '@/hooks/useCompactMode';
import { usePremium } from '@/hooks/usePremium';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { getLoadingDocuments, LoadingDocument, removeLoadingDocument } from '@/utils/loadingDocuments';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { sessionApi, Session } from '@/services/api';
import { useSessionStore, useNotificationStore } from '@/stores';
import { SessionDetailsModal } from './SessionDetailsModal';

interface SidebarProps {
  activePage: 'chat' | 'documents' | 'examination' | 'sessions';
  onPageChange: (page: 'chat' | 'documents' | 'examination' | 'sessions') => void;
  onDocumentSelect: (doc: any) => void;
  onAddDocuments?: () => void;
  onNewSession?: () => void;
  onSaveSession?: () => void;
  onSaveAsSession?: () => void;
  showSaveButton?: boolean;
  showSaveAsButton?: boolean;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

interface SessionContext {
  mpep_sections?: string[];
  cfr_sections?: string[];
  usc_sections?: string[];
  fed_reg_sections?: string[];
  pros_docs?: Array<{ docId: string; description?: string }>;
  current_patents?: string[];
  current_applications?: string[];
  current_publications?: string[];
  current_foreign_patents?: string[];
  resource_ids?: string[];
  resource_names?: string[];
  last_context_type?: string;
  last_context_number?: string;
}

export function Sidebar({ activePage, onPageChange, onDocumentSelect, onAddDocuments, onNewSession, onSaveSession, onSaveAsSession, showSaveButton, showSaveAsButton, collapsed = false, onToggleCollapse }: SidebarProps) {
  const { compactMode } = useCompactMode();
  const { isPremium } = usePremium();
  const { addNotification } = useNotificationStore();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [folders, setFolders] = useState<Array<{ folder_id: string; folder_name: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [sessionContext, setSessionContext] = useState<SessionContext | null>(null);
  const [sessionsExpanded, setSessionsExpanded] = useState(true);
  const [documentsExpanded, setDocumentsExpanded] = useState(true);
  const [showSessionSwitchDialog, setShowSessionSwitchDialog] = useState(false);
  const [pendingSession, setPendingSession] = useState<Session | null>(null);
  const [loadingSession, setLoadingSession] = useState(false);
  const [detailsSession, setDetailsSession] = useState<Session | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showClearAllModal, setShowClearAllModal] = useState(false);
  const [loadingDocuments, setLoadingDocuments] = useState<LoadingDocument[]>([]);
  const [showRenameDialog, setShowRenameDialog] = useState(false);
  const [renameSession, setRenameSession] = useState<Session | null>(null);
  const [newSessionName, setNewSessionName] = useState('');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteSession, setDeleteSession] = useState<Session | null>(null);
  const [showActiveDocsInfoModal, setShowActiveDocsInfoModal] = useState(false);
  const [showSessionsInfoModal, setShowSessionsInfoModal] = useState(false);

  const { setSessionId, setSessionName, setChatMessages, setSessionHasBeenSaved, setSavedFolderForSession, sessionId, resetSession } = useSessionStore();

  useEffect(() => {
    loadSessions();
    loadSessionContext();
    loadLoadingDocuments();

    // Listen for context updates
    const handleStorageUpdate = () => loadSessionContext();
    window.addEventListener('storage-update', handleStorageUpdate);
    window.addEventListener('storage', loadSessionContext);

    // Listen for session list updates
    const handleSessionsUpdate = () => loadSessions();
    window.addEventListener('sessions-updated', handleSessionsUpdate);

    // Listen for loading documents updates
    const handleLoadingDocsUpdate = () => {
      console.log('[Sidebar] loading-docs-updated event received, reloading...');
      loadLoadingDocuments();
    };
    window.addEventListener('loading-docs-updated', handleLoadingDocsUpdate);

    // Listen for job completion events
    // Note: Notifications are handled globally in Main.tsx, this only handles cleanup
    const handleJobComplete = (event: CustomEvent) => {
      const { jobId, docType, docId, success, error } = event.detail;
      console.log('[Sidebar] Job completion event received:', event.detail);

      // Format type name properly (handle foreign_patent -> Foreign Patent)
      const resourceTypeName = docType === 'foreign_patent'
        ? 'Foreign Patent'
        : docType.charAt(0).toUpperCase() + docType.slice(1);

      if (success) {
        // Remove from loading documents
        console.log('[Sidebar] Calling removeLoadingDocument with:', { docId, resourceTypeName });
        console.log('[Sidebar] Current loading documents before removal:', getLoadingDocuments());
        removeLoadingDocument(docId, resourceTypeName);
        console.log('[Sidebar] Current loading documents after removal:', getLoadingDocuments());
      } else {
        // Remove from loading documents
        removeLoadingDocument(docId, resourceTypeName);

        // Remove from context
        const stored = localStorage.getItem('session_context');
        const context = stored && stored !== "undefined" ? JSON.parse(stored) : {};

        if (docType === 'patent') {
          context.current_patents = (context.current_patents || []).filter((id: string) => id !== docId);
        } else if (docType === 'publication') {
          context.current_publications = (context.current_publications || []).filter((id: string) => id !== docId);
        } else if (docType === 'application') {
          context.current_applications = (context.current_applications || []).filter((id: string) => id !== docId);
        } else if (docType === 'foreign_patent') {
          context.current_foreign_patents = (context.current_foreign_patents || []).filter((id: string) => id !== docId);
        }

        localStorage.setItem('session_context', JSON.stringify(context));
        window.dispatchEvent(new Event('storage-update'));
      }
    };

    window.addEventListener('job-complete', handleJobComplete as EventListener);

    return () => {
      window.removeEventListener('storage-update', handleStorageUpdate);
      window.removeEventListener('storage', loadSessionContext);
      window.removeEventListener('sessions-updated', handleSessionsUpdate);
      window.removeEventListener('loading-docs-updated', handleLoadingDocsUpdate);
      window.removeEventListener('job-complete', handleJobComplete as EventListener);
    };
  }, []);

  // Reload sessions when current sessionId changes
  useEffect(() => {
    loadSessions();
  }, [sessionId]);

  // Debug: Log when loadingDocuments state changes
  useEffect(() => {
    console.log('[Sidebar] loadingDocuments state changed:', loadingDocuments);
  }, [loadingDocuments]);

  const loadSessionContext = () => {
    const storedData = localStorage.getItem('session_context');
    if (storedData && storedData !== "undefined") {
      try {
        const parsedData = JSON.parse(storedData);
        setSessionContext(parsedData);
      } catch (error) {
        console.error('Failed to parse session context:', error);
      }
    } else {
      setSessionContext(null);
    }
  };

  const loadLoadingDocuments = () => {
    const docs = getLoadingDocuments();
    console.log('[Sidebar] loadLoadingDocuments - setting state to:', docs);
    setLoadingDocuments(docs);
  };

  const removeFromActiveDocuments = (docId: string, docType: string) => {
    try {
      const stored = localStorage.getItem('session_context');
      const context = stored && stored !== "undefined" ? JSON.parse(stored) : {};

      // Determine which array to remove from based on document type
      if (docType === 'Patent' && context.current_patents) {
        context.current_patents = context.current_patents.filter((id: string) => id !== docId);
      } else if (docType === 'Application' && context.current_applications) {
        context.current_applications = context.current_applications.filter((id: string) => id !== docId);
      } else if (docType === 'Publication' && context.current_publications) {
        context.current_publications = context.current_publications.filter((id: string) => id !== docId);
      } else if (docType === 'Foreign Patent' && context.current_foreign_patents) {
        context.current_foreign_patents = context.current_foreign_patents.filter((id: string) => id !== docId);
      } else if (docType === 'Upload' && context.resource_names) {
        // Find the index before removing
        const index = context.resource_names.indexOf(docId);

        // Remove from resource_names
        context.resource_names = context.resource_names.filter((name: string) => name !== docId);

        // Also remove from resource_ids at the same index
        if (context.resource_ids && index !== -1) {
          context.resource_ids = context.resource_ids.filter((_: string, i: number) => i !== index);
        }
      } else if (docType === 'MPEP' && context.mpep_sections) {
        const sectionNum = docId.replace('MPEP ', '');
        context.mpep_sections = context.mpep_sections.filter((section: string) => section !== sectionNum);
      } else if (docType === 'CFR' && context.cfr_sections) {
        const sectionNum = docId.replace('37 CFR ', '');
        context.cfr_sections = context.cfr_sections.filter((section: string) => section !== sectionNum);
      } else if (docType === 'USC' && context.usc_sections) {
        const sectionNum = docId.replace('35 USC ', '');
        context.usc_sections = context.usc_sections.filter((section: string) => section !== sectionNum);
      } else if (docType === 'Draft') {
        // Remove from active drafts
        const activeDrafts = JSON.parse(localStorage.getItem('active_drafts') || '[]');
        const updatedDrafts = activeDrafts.filter((draft: any) => draft.draft_id !== docId);
        localStorage.setItem('active_drafts', JSON.stringify(updatedDrafts));
        // Also update session context to reflect the removal
        context.current_drafts = updatedDrafts;
      }

      localStorage.setItem('session_context', JSON.stringify(context));
      window.dispatchEvent(new Event('storage-update'));
    } catch (error) {
      console.error('Failed to remove from active documents:', error);
    }
  };

  const clearAllActiveDocuments = () => {
    setShowClearAllModal(true);
  };

  const handleConfirmClearAll = () => {
    try {
      const stored = localStorage.getItem('session_context');
      const context = stored && stored !== "undefined" ? JSON.parse(stored) : {};

      // Clear all document arrays
      context.current_patents = [];
      context.current_applications = [];
      context.current_publications = [];
      context.current_foreign_patents = [];
      context.resource_ids = [];
      context.resource_names = [];
      context.new_resource_ids = [];
      context.new_resource_names = [];
      context.mpep_sections = [];
      context.cfr_sections = [];
      context.usc_sections = [];
      context.fed_reg_sections = [];
      context.pros_docs = [];
      context.patent_numbers_dict_arr = [];
      context.pros_doc_names = [];
      context.extracted_resources = [];
      context.current_drafts = [];

      // Clear singular document references
      context.current_patent = null;
      context.current_publication = null;
      context.current_application = null;
      context.last_context_type = null;
      context.last_context_number = null;

      localStorage.setItem('session_context', JSON.stringify(context));

      // Clear active drafts
      localStorage.setItem('active_drafts', JSON.stringify([]));

      // Also clear separate localStorage keys
      localStorage.removeItem('new_resource_ids');
      localStorage.removeItem('new_resource_names');

      window.dispatchEvent(new Event('storage-update'));
      setShowClearAllModal(false);
    } catch (error) {
      console.error('Failed to clear all active documents:', error);
    }
  };

  const loadSessions = async () => {
    setLoading(true);
    try {
      // Fetch both sessions and folders
      const [fetchedSessions, fetchedFolders] = await Promise.all([
        sessionApi.fetchSessions(),
        sessionApi.fetchFolders()
      ]);

      setFolders(fetchedFolders);

      // Map folder_name to sessions
      const sessionsWithFolderNames = fetchedSessions.map(session => {
        if (session.folder_id) {
          const folder = fetchedFolders.find(f => f.folder_id === session.folder_id);
          return {
            ...session,
            folder_name: folder?.folder_name
          };
        }
        return session;
      });

      // Filter out background sessions
      const filteredSessions = sessionsWithFolderNames
        .filter(s => !s.session_id?.startsWith('background_') && s.session_name !== 'where you left off');

      // Always show current session at the top if it exists
      const currentSessionId = sessionId;
      let finalSessions: Session[] = [];

      if (currentSessionId) {
        const currentSession = filteredSessions.find(s => s.session_id === currentSessionId);
        if (currentSession) {
          // Add current session first
          finalSessions.push(currentSession);
          // Add up to 4 more sessions (excluding current)
          const otherSessions = filteredSessions
            .filter(s => s.session_id !== currentSessionId)
            .slice(0, 4);
          finalSessions.push(...otherSessions);
        } else {
          // Current session not found in list, just show top 5
          finalSessions = filteredSessions.slice(0, 5);
        }
      } else {
        // No current session, show top 5
        finalSessions = filteredSessions.slice(0, 5);
      }

      setSessions(finalSessions);
    } catch (error) {
      console.error('Failed to load sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSessionClick = (session: Session) => {
    // Check if there are active documents in current session
    const hasActiveDocuments = sessionContext && (
      (sessionContext.current_patents?.length || 0) > 0 ||
      (sessionContext.current_applications?.length || 0) > 0 ||
      (sessionContext.current_publications?.length || 0) > 0 ||
      (sessionContext.current_foreign_patents?.length || 0) > 0 ||
      (sessionContext.resource_names?.length || 0) > 0 ||
      (sessionContext.mpep_sections?.length || 0) > 0 ||
      (sessionContext.cfr_sections?.length || 0) > 0 ||
      (sessionContext.usc_sections?.length || 0) > 0
    );

    if (hasActiveDocuments) {
      // Show confirmation dialog
      setPendingSession(session);
      setShowSessionSwitchDialog(true);
    } else {
      // Load session directly
      loadSession(session);
    }
  };

  const loadSession = async (session: Session) => {
    setLoadingSession(true);
    try {
      // Fetch full session with chat history
      const fullSession = await sessionApi.fetchSessionWithHistory(session.session_id);

      if (fullSession) {
        // Update session store
        setSessionId(fullSession.session_id);
        setSessionName(fullSession.session_name);
        setSessionHasBeenSaved(true); // Mark as saved since we loaded it from backend

        // Set folder name if session is in a folder
        if (fullSession.folder_id) {
          const folder = folders.find(f => f.folder_id === fullSession.folder_id);
          if (folder) {
            setSavedFolderForSession(folder.folder_name);
            localStorage.setItem('folder_name', folder.folder_name);
          } else {
            console.error('Folder not found for folder_id:', fullSession.folder_id);
          }
        }

        // Update chat messages if available
        if (fullSession.chat_history && Array.isArray(fullSession.chat_history)) {
          setChatMessages(normalizeChatHistory(fullSession.chat_history) as any);
        }

        // Update session context in localStorage if available
        if (fullSession.session_context) {
          localStorage.setItem('session_context', JSON.stringify(fullSession.session_context));

          // Update active drafts from session context
          if (fullSession.session_context.current_drafts) {
            console.log('[Sidebar] Loading session with drafts:', fullSession.session_context.current_drafts);
            localStorage.setItem('active_drafts', JSON.stringify(fullSession.session_context.current_drafts));
          } else {
            console.log('[Sidebar] Loading session with no drafts, clearing active_drafts');
            // Clear active drafts if session has none
            localStorage.setItem('active_drafts', JSON.stringify([]));
          }

          window.dispatchEvent(new Event('storage-update'));
        }

        // Navigate to chat page if not already there
        if (activePage !== 'chat') {
          onPageChange('chat');
        }
      }
    } catch (error) {
      console.error('Failed to load session:', error);
      alert('Failed to load session. Please try again.');
    } finally {
      setLoadingSession(false);
    }
  };

  const handleConfirmSessionSwitch = async () => {
    setShowSessionSwitchDialog(false);
    if (pendingSession) {
      await loadSession(pendingSession);
      setPendingSession(null);
    }
  };

  const handleCancelSessionSwitch = () => {
    setPendingSession(null);
    setShowSessionSwitchDialog(false);
  };

  const handleViewDetails = (session: Session, e: React.MouseEvent) => {
    e.stopPropagation();
    setDetailsSession(session);
    setShowDetailsModal(true);
  };

  const handleRenameClick = (session: Session, e: React.MouseEvent) => {
    e.stopPropagation();
    setRenameSession(session);
    setNewSessionName(session.session_name || '');
    setShowRenameDialog(true);
  };

  const handleConfirmRename = async () => {
    if (!renameSession || !newSessionName.trim()) return;

    try {
      await sessionApi.renameSession(renameSession.session_id, newSessionName.trim());

      // Update the session in the list
      setSessions(prev => prev.map(s =>
        s.session_id === renameSession.session_id
          ? { ...s, session_name: newSessionName.trim() }
          : s
      ));

      // If renaming current session, update the store
      if (renameSession.session_id === sessionId) {
        setSessionName(newSessionName.trim());
      }

      setShowRenameDialog(false);
      setRenameSession(null);
      setNewSessionName('');
    } catch (error) {
      console.error('Failed to rename session:', error);
      // TODO: Show error notification
    }
  };

  const handleDeleteClick = (session: Session, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeleteSession(session);
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteSession) return;

    const isDeletingCurrentSession = deleteSession.session_id === sessionId;

    try {
      await sessionApi.deleteSession(deleteSession.session_id);

      // Remove from the list
      setSessions(prev => prev.filter(s => s.session_id !== deleteSession.session_id));

      setShowDeleteDialog(false);
      setDeleteSession(null);

      // If deleting current session, start a new session
      if (isDeletingCurrentSession) {
        // Clear session context from localStorage
        localStorage.removeItem('session_context');

        // Reset to a fresh session with welcome message
        resetSession();

        // Reload to ensure everything is properly initialized
        window.location.reload();
      }
    } catch (error) {
      console.error('Failed to delete session:', error);
      // TODO: Show error notification
    }
  };

  const handleFolderClick = (folderId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    // Store the folder_id in localStorage to open it in SessionsPage
    localStorage.setItem('open_folder_id', folderId);
    // Navigate to sessions page
    onPageChange('sessions');
  };

  const formatDate = (dateString: string) => {
    // DDB timestamps are in UTC format "2025-10-29 14:29:35.889"
    // Convert to ISO format by replacing space with 'T' and adding 'Z' for UTC
    const isoString = dateString.replace(' ', 'T') + 'Z';
    const date = new Date(isoString);
    const now = new Date();

    // Compare calendar days in local timezone, not absolute time differences
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterdayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
    const dateStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());

    if (dateStart.getTime() === todayStart.getTime()) {
      return `Today, ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
    } else if (dateStart.getTime() === yesterdayStart.getTime()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  // Get active documents from current session context (like ContextSummary)
  const activeDocuments: Array<{
    id: string;
    title: string;
    type: string;
    status: string;
    resource_id?: string;
    file_type?: string;
    file_name?: string;
  }> = [];

  if (sessionContext) {
    // Patents
    (sessionContext.current_patents || []).filter(Boolean).forEach((id, i) => {
      activeDocuments.push({
        id: `patent-${i}`,
        title: id,
        type: 'Patent',
        status: 'Active',
        resource_id: `patent-${id}`,
        file_type: 'patent',
        file_name: `Patent ${id}`
      });
    });

    // Applications
    (sessionContext.current_applications || []).filter(Boolean).forEach((id, i) => {
      activeDocuments.push({
        id: `app-${i}`,
        title: id,
        type: 'Application',
        status: 'Active',
        resource_id: `application-${id}`,
        file_type: 'application',
        file_name: `Application ${id}`
      });
    });

    // Publications
    (sessionContext.current_publications || []).filter(Boolean).forEach((id, i) => {
      activeDocuments.push({
        id: `pub-${i}`,
        title: id,
        type: 'Publication',
        status: 'Active',
        resource_id: `publication-${id}`,
        file_type: 'publication',
        file_name: `Publication ${id}`
      });
    });

    // Foreign Patents
    (sessionContext.current_foreign_patents || []).filter(Boolean).forEach((id, i) => {
      activeDocuments.push({
        id: `foreign-patent-${i}`,
        title: id,
        type: 'Foreign Patent',
        status: 'Active',
        resource_id: `foreign_patent-${id}`,
        file_type: 'foreign_patent',
        file_name: `Foreign Patent ${id}`
      });
    });

    // Uploads
    (sessionContext.resource_names || []).filter(Boolean).forEach((name, i) => {
      const resourceId = sessionContext.resource_ids?.[i] || name;
      activeDocuments.push({
        id: `upload-${i}`,
        title: name,
        type: 'Upload',
        status: 'Active',
        resource_id: resourceId,
        file_type: 'upload',
        file_name: name
      });
    });

    // MPEP Sections
    (sessionContext.mpep_sections || []).filter(Boolean).forEach((section, i) => {
      activeDocuments.push({ id: `mpep-${i}`, title: `MPEP ${section}`, type: 'MPEP', status: 'Active' });
    });

    // CFR Sections
    (sessionContext.cfr_sections || []).filter(Boolean).forEach((section, i) => {
      activeDocuments.push({ id: `cfr-${i}`, title: `37 CFR ${section}`, type: 'CFR', status: 'Active' });
    });

    // USC Sections
    (sessionContext.usc_sections || []).filter(Boolean).forEach((section, i) => {
      activeDocuments.push({
        id: `usc-${i}`,
        title: `35 USC ${section}`,
        type: 'USC',
        status: 'Active',
        resource_id: section,
        section: section
      });
    });
  }

  // Add active drafts from localStorage
  try {
    const activeDrafts = JSON.parse(localStorage.getItem('active_drafts') || '[]');
    activeDrafts.forEach((draft: any, i: number) => {
      activeDocuments.push({
        id: `draft-${draft.draft_id}`,
        title: draft.title || 'Untitled Draft',
        type: 'Draft',
        status: 'Active',
        resource_id: draft.draft_id,
        file_type: 'draft',
        file_name: draft.title
      });
    });
  } catch (error) {
    console.error('Failed to load active drafts:', error);
  }

  return (
    <div className={`border-r border-gray-200 bg-white flex flex-col h-full overflow-hidden transition-all duration-300 ${
      collapsed ? 'w-0' : compactMode ? 'w-[220px]' : 'w-[260px]'
    }`}>
      {/* Session Controls */}
      <div className={`flex-shrink-0 border-b border-gray-200 ${compactMode ? 'p-2' : 'p-3'}`}>
        <div className={`flex items-center ${compactMode ? 'gap-1' : 'gap-2'} ${collapsed ? 'justify-center' : ''}`}>
          {!collapsed && (
            <>
              <Button
                variant="outline"
                size={compactMode ? 'sm' : 'sm'}
                onClick={onNewSession}
                className={compactMode ? 'flex-1 gap-1 text-xs h-7 px-2' : 'flex-1 gap-1.5 text-xs h-8'}
              >
                <Plus className={compactMode ? 'w-3 h-3' : 'w-3.5 h-3.5'} />
                {compactMode ? 'New' : 'New Session'}
              </Button>
              {showSaveButton && (
                <Button
                  variant="outline"
                  size={compactMode ? 'sm' : 'sm'}
                  onClick={onSaveSession}
                  className={compactMode ? 'gap-1 text-xs h-7 px-1.5' : 'gap-1.5 text-xs h-8 px-2'}
                  title="Save Session"
                >
                  <Save className={compactMode ? 'w-3 h-3' : 'w-3.5 h-3.5'} />
                </Button>
              )}
              {showSaveAsButton && (
                <Button
                  variant="outline"
                  size={compactMode ? 'sm' : 'sm'}
                  onClick={onSaveAsSession}
                  className={compactMode ? 'gap-1 text-xs h-7 px-1.5' : 'gap-1.5 text-xs h-8 px-2'}
                  title="Save As..."
                >
                  <SaveAll className={compactMode ? 'w-3 h-3' : 'w-3.5 h-3.5'} />
                </Button>
              )}
              <Button
                variant="ghost"
                size={compactMode ? 'sm' : 'sm'}
                onClick={() => window.dispatchEvent(new Event('open-onboarding-wizard'))}
                className={compactMode ? 'gap-1 text-xs h-7 px-1.5' : 'gap-1.5 text-xs h-8 px-2'}
                title="Wizard"
              >
                <Sparkles className={compactMode ? 'w-3 h-3' : 'w-3.5 h-3.5'} />
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Scrollable Content Area */}
      <div className={`flex-1 overflow-hidden ${collapsed ? 'hidden' : ''}`}>
        <ScrollArea className="h-full">
          <div className={compactMode ? 'p-2 space-y-2' : 'p-4 space-y-4'}>
            {/* Recent Sessions Section */}
            <div>
              <div className={`flex items-center justify-between ${compactMode ? 'mb-1' : 'mb-2'}`}>
                <button
                  onClick={() => setSessionsExpanded(!sessionsExpanded)}
                  className={`flex items-center gap-1 ${compactMode ? 'text-[10px]' : 'text-xs'} text-gray-500 uppercase tracking-wider hover:text-gray-700`}
                >
                  {sessionsExpanded ? <ChevronDown className={compactMode ? 'w-2.5 h-2.5' : 'w-3 h-3'} /> : <ChevronUp className={compactMode ? 'w-2.5 h-2.5' : 'w-3 h-3'} />}
                  Recent Sessions
                </button>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => onPageChange('sessions')}
                    className={`${compactMode ? 'text-[10px]' : 'text-xs'} text-blue-600 hover:text-blue-700 font-medium`}
                  >
                    View All
                  </button>
                  <Button
                    onClick={() => setShowSessionsInfoModal(true)}
                    size="sm"
                    variant="outline"
                    className={compactMode ? 'h-5 w-5 p-0 text-xs font-semibold text-gray-600 border-gray-300 hover:bg-gray-100 flex-shrink-0' : 'h-6 w-6 p-0 text-xs font-semibold text-gray-600 border-gray-300 hover:bg-gray-100 flex-shrink-0'}
                    title="What are sessions?"
                  >
                    ?
                  </Button>
                </div>
              </div>

              {sessionsExpanded && (
                loading ? (
                  <div className={`text-center ${compactMode ? 'text-xs py-1' : 'text-sm py-2'} text-gray-500`}>Loading...</div>
                ) : loadingSession ? (
                  <div className={`text-center ${compactMode ? 'text-xs py-1' : 'text-sm py-2'} text-gray-500`}>Loading session...</div>
                ) : sessions.length === 0 ? (
                  <div className={`text-center ${compactMode ? 'text-xs py-1' : 'text-sm py-2'} text-gray-500`}>No sessions yet</div>
                ) : (
                  <div className={compactMode ? 'space-y-0.5' : 'space-y-1'}>
                    {sessions.map((session) => {
                      const isActive = session.session_id === sessionId;
                      return (
                        <div
                          key={session.session_id}
                          className={`group relative ${compactMode ? 'p-1.5' : 'p-2'} rounded-lg cursor-pointer transition-colors ${
                            isActive
                              ? 'bg-blue-50 border border-blue-200'
                              : 'hover:bg-gray-50 border border-transparent'
                          }`}
                          onClick={() => handleSessionClick(session)}
                        >
                          <div className={`flex items-center justify-between ${compactMode ? 'gap-1' : 'gap-2'}`}>
                            <div className={`flex items-center ${compactMode ? 'gap-1' : 'gap-2'} flex-1 min-w-0`}>
                              <MessageSquare className={`${compactMode ? 'w-2.5 h-2.5' : 'w-3 h-3'} flex-shrink-0 ${isActive ? 'text-blue-600' : 'text-gray-400'}`} />
                              <div className="flex-1 min-w-0">
                                <span className={`${compactMode ? 'text-[11px]' : 'text-xs'} truncate block ${isActive ? 'text-gray-900 font-medium' : 'text-gray-700'}`}>
                                {session.session_name}
                              </span>
                              <div className={`flex items-center ${compactMode ? 'gap-1' : 'gap-1.5'}`}>
                                {session.folder_name && session.folder_id && (
                                  <span
                                    className={`${compactMode ? 'text-[10px]' : 'text-xs'} text-blue-600 truncate cursor-pointer hover:underline flex items-center gap-0.5`}
                                    onClick={(e) => handleFolderClick(session.folder_id!, e)}
                                    title="Open folder in Sessions page"
                                  >
                                    <Folder className={compactMode ? 'w-2.5 h-2.5' : 'w-3 h-3'} />
                                    {session.folder_name}
                                  </span>
                                )}
                                <span className={`${compactMode ? 'text-[10px]' : 'text-xs'} ${session.folder_name ? 'text-gray-500' : 'text-gray-500'}`}>
                                  {session.folder_name && '•'} {formatDate(session.last_modified)}
                                </span>
                              </div>
                            </div>
                          </div>

                          <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className={`${compactMode ? 'h-4 w-4' : 'h-5 w-5'} opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0`}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <MoreVertical className={compactMode ? 'w-2.5 h-2.5' : 'w-3 h-3'} />
                                </Button>
                              </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="bg-white border border-gray-200 shadow-lg">
                              <DropdownMenuItem
                                onClick={(e) => handleViewDetails(session, e)}
                                className="text-gray-700 hover:bg-gray-100 cursor-pointer"
                              >
                                <Eye className="w-4 h-4 mr-2" />
                                View Details
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={(e) => handleRenameClick(session, e)}
                                className="text-gray-700 hover:bg-gray-100 cursor-pointer"
                              >
                                <Edit2 className="w-4 h-4 mr-2" />
                                Rename
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={(e) => handleDeleteClick(session, e)}
                                className="text-red-600 hover:bg-gray-100 cursor-pointer"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    );
                    })}
                  </div>
                )
              )}
            </div>

            {/* Active Documents Section */}
            <div>
              <div className={`flex items-center justify-between ${compactMode ? 'mb-1' : 'mb-2'}`}>
                <button
                  onClick={() => setDocumentsExpanded(!documentsExpanded)}
                  className={`flex items-center gap-1 ${compactMode ? 'text-[10px]' : 'text-xs'} text-gray-500 uppercase tracking-wider hover:text-gray-700`}
                >
                  {documentsExpanded ? <ChevronDown className={compactMode ? 'w-2.5 h-2.5' : 'w-3 h-3'} /> : <ChevronUp className={compactMode ? 'w-2.5 h-2.5' : 'w-3 h-3'} />}
                  Active Documents
                </button>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <Button
                    onClick={onAddDocuments}
                    size="sm"
                    className={compactMode ? 'h-5 w-5 p-0 bg-blue-600 hover:bg-blue-700 text-white flex-shrink-0' : 'h-6 w-6 p-0 bg-blue-600 hover:bg-blue-700 text-white flex-shrink-0'}
                    title="Add documents"
                  >
                    <Plus className={compactMode ? 'w-2.5 h-2.5' : 'w-3 h-3'} />
                  </Button>
                  <Button
                    onClick={clearAllActiveDocuments}
                    size="sm"
                    variant="outline"
                    className={compactMode ? 'h-5 w-5 p-0 border-gray-300 text-gray-600 hover:bg-gray-100 flex-shrink-0' : 'h-6 w-6 p-0 border-gray-300 text-gray-600 hover:bg-gray-100 flex-shrink-0'}
                    disabled={activeDocuments.length === 0}
                    title="Clear all documents"
                  >
                    <Trash2 className={compactMode ? 'w-2.5 h-2.5' : 'w-3 h-3'} />
                  </Button>
                  <Button
                    onClick={() => setShowActiveDocsInfoModal(true)}
                    size="sm"
                    variant="outline"
                    className={compactMode ? 'h-5 w-5 p-0 text-xs font-semibold text-gray-600 border-gray-300 hover:bg-gray-100 flex-shrink-0' : 'h-6 w-6 p-0 text-xs font-semibold text-gray-600 border-gray-300 hover:bg-gray-100 flex-shrink-0'}
                    title="What are Active Documents?"
                  >
                    ?
                  </Button>
                </div>
              </div>

              {documentsExpanded && (
                activeDocuments.length === 0 ? (
                  <div className={`text-center ${compactMode ? 'text-xs py-1' : 'text-sm py-2'} text-gray-500`}>No active documents</div>
                ) : (
                  <div className={compactMode ? 'space-y-0.5' : 'space-y-1'}>
                    {activeDocuments.map((doc) => {
                      // For uploads, match by title/name; for others, match by type and title (which is the doc ID)
                      const isLoading = loadingDocuments.some(loadingDoc => {
                        if (doc.type === 'Upload') {
                          return loadingDoc.type === 'Upload' && loadingDoc.title === doc.title;
                        }
                        return `${loadingDoc.type}-${loadingDoc.id}` === `${doc.type}-${doc.title}`;
                      });

                      return (
                        <div
                          key={doc.id}
                          className={`group ${compactMode ? 'p-1.5' : 'p-2'} rounded-lg hover:bg-gray-50 border border-transparent hover:border-gray-200 transition-colors`}
                        >
                          <div className={`flex items-center ${compactMode ? 'gap-1' : 'gap-2'}`}>
                            <Folder className={`${compactMode ? 'w-2.5 h-2.5' : 'w-3 h-3'} text-gray-400 flex-shrink-0`} />
                            <div
                              className="flex-1 min-w-0 cursor-pointer"
                              onClick={() => {
                                console.log('Sidebar Active Documents click - passing document:', doc);
                                // Handle draft clicks differently - store in localStorage for ChatArea to pick up
                                if (doc.type === 'Draft') {
                                  // Check premium access for drafts
                                  if (!isPremium) {
                                    addNotification({
                                      type: 'warning',
                                      title: 'Premium Feature',
                                      message: 'Drafts is a premium-only feature. Please upgrade your account to access this functionality.',
                                      duration: 5000
                                    });
                                    return;
                                  }
                                  localStorage.setItem('open_draft_id', doc.resource_id || '');
                                  window.dispatchEvent(new Event('open-draft'));
                                } else {
                                  onDocumentSelect(doc);
                                }
                              }}
                            >
                              <div className={`flex items-center ${compactMode ? 'gap-1' : 'gap-1.5'}`}>
                                <div className={`${compactMode ? 'text-[11px]' : 'text-xs'} text-gray-900 truncate`}>{doc.title}</div>
                                {isLoading && (
                                  <Loader2 className={`${compactMode ? 'w-2.5 h-2.5' : 'w-3 h-3'} text-blue-600 animate-spin flex-shrink-0`} />
                                )}
                              </div>
                              <div className={`${compactMode ? 'text-[10px]' : 'text-xs'} text-gray-500`}>{doc.type}</div>
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              className={`${compactMode ? 'h-4 w-4' : 'h-5 w-5'} opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0`}
                              onClick={(e) => {
                                e.stopPropagation();
                                // For drafts, use resource_id (draft_id), for others use title
                                const identifier = doc.type === 'Draft' ? doc.resource_id : doc.title;
                                removeFromActiveDocuments(identifier, doc.type);
                              }}
                            >
                              <X className={`${compactMode ? 'w-2.5 h-2.5' : 'w-3 h-3'} text-gray-500 hover:text-red-600`} />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )
              )}
            </div>
          </div>
        </ScrollArea>
      </div>

      {/* Sessions Info Dialog */}
      <Dialog open={showSessionsInfoModal} onOpenChange={setShowSessionsInfoModal}>
        <DialogContent className="bg-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-gray-900">What is a Session?</DialogTitle>
            <DialogDescription className="text-gray-600">
              Sessions capture a snapshot of your patent work, including chat history and active documents, so you can return to
              it later or share it.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 text-sm text-gray-600">
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">What’s saved</h4>
              <ul className="list-disc pl-5 space-y-1.5">
                <li>Chat messages and AI responses</li>
                <li>Active Documents and USPTO resources tied to the conversation</li>
                <li>Task panels, drafts, and any notes generated in the workflow</li>
              </ul>
            </div>
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">Build and reuse sessions</h4>
              <ul className="list-disc pl-5 space-y-2">
                <li>Use <strong>New Session</strong> in the sidebar to start a blank workspace for a different matter.</li>
                <li>Click <strong>Save</strong> or <strong>Save As</strong> (top-right of the sidebar) to persist your current work.</li>
                <li>In the Documents or USPTO tabs, add materials to the Active Documents list—they stay linked to this session.</li>
              </ul>
            </div>
            <p className="text-xs text-gray-500">
              Tip: rename sessions or organize them into folders so your team can quickly locate the right workspace later.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Active Documents Info Dialog */}
      <Dialog open={showActiveDocsInfoModal} onOpenChange={setShowActiveDocsInfoModal}>
        <DialogContent className="bg-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-gray-900">What are Active Documents?</DialogTitle>
            <DialogDescription className="text-gray-600">
              These are the patents, patent publications, uploads, resources (MPEP, 37 CFR, 35 USC) currently linked to this session. They are attached to every chat
              prompt and power citations inside results.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 text-sm text-gray-600">
            <div className="rounded-lg border border-gray-200 p-4 bg-gray-50">
              <h4 className="text-xs font-semibold uppercase text-gray-500 tracking-wider mb-2">How they’re used</h4>
              <p>
                When you send a message, the assistant pulls content from the listed Active Documents (and any you mention with
                <code> @</code>) to answer with grounded, citeable responses.
              </p>
            </div>
            <div>
              <h4 className="text-xs font-semibold uppercase text-gray-500 tracking-wider mb-2">Add documents to this session</h4>
              <ul className="list-disc pl-5 space-y-2">
                <li>
                  Click <strong>Add documents</strong> here in the sidebar to search uploads, USPTO records, or other sources and
                  attach them.
                </li>
                <li>
                  From Documents or USPTO tabs, use the <strong>Activate/Add to session</strong> actions to push a file into this
                  list.
                </li>
                <li>
                  Mention a document inline in chat with <code>@DocumentName</code>; if it is available, it becomes active for that
                  session.
                </li>
              </ul>
            </div>
            <p className="text-xs text-gray-500">
              Remove items from the list to keep prompts concise—anything left here travels with the assistant’s context.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Session Switch Confirmation Dialog */}
      <Dialog open={showSessionSwitchDialog} onOpenChange={setShowSessionSwitchDialog}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="text-gray-900">Switch Session</DialogTitle>
            <DialogDescription className="text-gray-600">
              You are about to load a different session. Any unsaved changes in the current session may be lost.
              {pendingSession && (
                <div className="mt-2 font-medium text-gray-900">
                  Load session: "{pendingSession.session_name}"?
                </div>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancelSessionSwitch} disabled={loadingSession}>
              Cancel
            </Button>
            <Button onClick={handleConfirmSessionSwitch} disabled={loadingSession}>
              {loadingSession ? 'Loading...' : 'Continue'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Session Details Modal */}
      <SessionDetailsModal
        open={showDetailsModal}
        onOpenChange={setShowDetailsModal}
        session={detailsSession}
      />

      {/* Clear All Documents Confirmation Dialog */}
      <Dialog open={showClearAllModal} onOpenChange={setShowClearAllModal}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="text-gray-900">Clear All Active Documents</DialogTitle>
            <DialogDescription className="text-gray-600">
              Are you sure you want to remove all active documents from this session? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowClearAllModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirmClearAll}
              variant="destructive"
              className="bg-red-600 hover:bg-red-700"
            >
              Clear All
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rename Session Dialog */}
      <Dialog open={showRenameDialog} onOpenChange={setShowRenameDialog}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="text-gray-900">Rename Session</DialogTitle>
            <DialogDescription className="text-gray-600">
              Enter a new name for this session.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input
              value={newSessionName}
              onChange={(e) => setNewSessionName(e.target.value)}
              placeholder="Session name"
              className="w-full"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleConfirmRename();
                }
              }}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRenameDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirmRename}
              disabled={!newSessionName.trim()}
            >
              Rename
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Session Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="text-gray-900">Delete Session</DialogTitle>
            <DialogDescription className="text-gray-600">
              Are you sure you want to delete "{deleteSession?.session_name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDelete}
              variant="destructive"
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
const normalizeChatHistory = (history: any[] = []) => {
  return history.map((message) => ({
    role: (message.role === 'assistant' || message.role === 'system' ? message.role : 'user') as 'user' | 'assistant' | 'system',
    content: message.content ?? '',
    receiveTime: message.receiveTime,
    sendTime: message.sendTime,
    message_context: message.message_context,
    session_context: message.session_context,
    token_usage: message.token_usage,
  }));
};
